import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Button, Image, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ImagePlaceholder from '~/components/ImagePlaceholder';
import EmblaSlider from '~/components/Slider/EmblaSlider';
import { useDropcart } from '~/context/DropcartContext';
import { useOrder } from '~/context/OrderContext';
import { useRoutePath } from '~/hooks/useRoutePath';
import { Product } from '~/types/Product';
import { apiFetch } from '~/utils/apiFetch';
import { getProductImages } from '~/utils/images';
import { useFormatPrice } from '~/utils/price';
import { useDefaultCurrency } from '~/utils/useChannelConfig';

const fetchUpsellProducts = async (): Promise<Product[]> => {
  const API_URL = window?.ENV?.API_URL || '';
  const response = await apiFetch(`${API_URL}/api/v2/shop/settings/app.upsell`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.settings?.products || [];
};

const DropcartProductSlider: React.FC = () => {
  const { t } = useTranslation();
  const routePath = useRoutePath();
  const { order, addToCart } = useOrder();
  const { closeDropcart } = useDropcart();
  const formatPrice = useFormatPrice();
  const defaultCurrency = useDefaultCurrency();
  const currencyCode = order?.currencyCode ?? defaultCurrency;
  const [addingProductId, setAddingProductId] = useState<number | null>(null);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['upsell-products'],
    queryFn: fetchUpsellProducts,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const getVariantCode = (product: Product): string | null => {
    if (product.defaultVariantData?.code) {
      return product.defaultVariantData.code;
    }
    if (product.defaultVariant) {
      const parts = product.defaultVariant.split('/');
      return parts[parts.length - 1] || null;
    }
    return null;
  };

  const handleAddToCart = async (product: Product) => {
    const variantCode = getVariantCode(product);
    if (!variantCode) return;

    setAddingProductId(product.id);
    try {
      await addToCart(variantCode, 1);
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setAddingProductId(null);
    }
  };

  if (isLoading || products.length === 0) return null;

  return (
    <div className="dropcart-slider mt-4">
      <p className="dropcart-slider__title fw-semibold mb-3 text-center">{t('components.dropcart.slider_title')}</p>
      <EmblaSlider slideClass="embla__slide--dropcart" showProgress={false}>
        {products.map((product) => {
          const variant = product.defaultVariantData;
          const image = getProductImages(product.images, 'small_thumbnail')[0]?.path;
          const hasDiscount = !!variant?.originalPrice && variant.originalPrice > variant.price;

          return (
            <React.Fragment key={product.id}>
              <Link
                to={routePath('product', product.slug)}
                className="dropcart-product__link fw-semibold text-center"
                onClick={closeDropcart}
              >
                {image ? (
                  <Image
                    src={image}
                    alt={product.name}
                    className="dropcart-product__image rounded-3 object-fit-cover"
                    loading="lazy"
                    fetchpriority="low"
                    decoding="async"
                    width={132}
                    height={132}
                  />
                ) : (
                  <ImagePlaceholder className="dropcart-product__image" />
                )}
                <div className="dropcart-product__name">{product.name}</div>
              </Link>
              <div className="dropcart-product__price d-flex align-items-center justify-content-center flex-wrap column-gap-1 text-center">
                {hasDiscount && (
                  <del className="dropcart-product__price-original text-muted ms-1">
                    {formatPrice(variant.originalPrice!, currencyCode)}
                  </del>
                )}
                <span className="dropcart-product__price-current fw-bold">
                  {variant?.price ? formatPrice(variant.price, currencyCode) : formatPrice(0, currencyCode)}
                </span>
                {product.variants.length > 1 ? (
                  <Link
                    to={routePath('product', product.slug)}
                    className="btn btn-outline-primary dropcart-product__add-btn w-100 mt-2 fw-semibold"
                    onClick={closeDropcart}
                  >
                    {t('components.dropcart.detail')}
                  </Link>
                ) : (
                  <Button
                    variant="outline-primary"
                    className="dropcart-product__add-btn w-100 mt-2 fw-semibold"
                    onClick={() => handleAddToCart(product)}
                    disabled={addingProductId === product.id || !getVariantCode(product)}
                  >
                    {addingProductId === product.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      t('components.dropcart.add')
                    )}
                  </Button>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </EmblaSlider>
    </div>
  );
};

export default DropcartProductSlider;
